import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import { formatNumberToUSD } from 'src/shared/calculators';
import { RootState } from '../../shared/state/root-reducer';
import { AppDispatch } from 'src/shared/state/store';
import { setMatchingRule } from 'src/patient/state/patient-slice';
import { patientService } from '../../patient/services/patient-service';
import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel, PatientCurrentInsurance } from '../../patient/components/models/patient-view-model';
import { showErrorStatus } from 'src/security/state/user-slice';
import { createProgram, updateProgram } from 'src/payment-hub/services/program-service';
import { createPatientNote } from 'src/patient/state/patient-thunk';
import { PatientNotes } from 'src/patient/components/models/patient-note';
import { axiosReadHelper } from '../../shared/service/axios-read-helper';
import { AxiosReadPayload } from '../../shared/service/axios-read-payload';
import { AxiosResultStatus } from '../../shared/service/axios-result-status';
import { admissionsAdvisorService } from '../../admissions-advisor/services/admissions-advisor-services';
import { useCustomProgramView, type CustomProgramContextAddition } from './use-custom-program-view';
import { useHubChannel } from '../hooks/use-hub-channel';

// Ensure get patient and get encounter are only called once per key per mount (avoids duplicate calls from Strict Mode or effect re-runs)
const fetchedPatientIds = new Set<number>();
const fetchedEncounterKeys = new Set<string>();

const useStyles = makeStyles((theme: any) => ({
  fullWidth: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '80%',
    },
    marginTop: '1rem',
    marginBottom: '1rem',
  },
}));

export interface PaymentProgramViewContext {
  // Params & routing
  isMobile: boolean;
  programType?: string;
  isCustomResults: boolean;
  currentProgramType: string;
  getProgramPath: (path: string) => string;
  patientId: number;
  encounterId: number;
  navigate: ReturnType<typeof useNavigate>;

  // Encounter & amounts
  activeEncounter: PatientEncounter | null | undefined;
  /** Full patient details (fetched by patientId when on hub route, or from Redux selectedPatient) */
  activePatient: PatientViewModel | null | undefined;
  /** Current insurance (patient-level) for payor display; fetched by patientId when on hub route, or from Redux */
  activePatientCurrentInsurance: PatientCurrentInsurance | null | undefined;
  /** Redux selected current insurance (fallback when fetch not yet loaded) */
  selectedPatientCurrentInsurance: { payorId: number } | null | undefined;
  /** Map of payorId -> payer for resolving payor name from current insurance */
  payersMap: Map<number, { payorName?: string }> | undefined;
  /** Estimated adjusted gross charges from the estimate linked by ioc.pfrEstimateId, when available */
  linkedEstimateAdjustedCharges: number | null;
  /** EIV from the linked estimate's risk assessment, when available */
  linkedEstimateEiv: number | null;
  pfrAmt: number;
  pfrCollected: number;
  remainingPfr: number;
  adjustedPfrAmt: number;

  // Rule-derived
  matchingRule: any;
  optDownPayment: number;
  /** Min preapproved down/recurring/term (from custom hook when custom view is used). */
  minDownPaymentPreapproved: number | null;
  minRecurringPreapproved: number | null;
  /** Min recurring at max preauth term using affordable down when DP field shown (prompt + preauth eligibility vs rule min). */
  minRecurringPreapprovedForPrompt: number | null;
  meetsPreauthMinimums: boolean;
  minTermPreapproved: number | null;
  optimalTermNum: number;
  /** Terms for display/API: when weekly, rules API terms (months) × 4 */
  optimalTermNumDisplay: number;
  recurringAmt: number;
  remainingDownPmt: number;

  // Loading
  isLoadingEncounter: boolean;
  isLoadingPatient: boolean;
  isLoadingPatientCurrentInsurance: boolean;
  isLoadingRule: boolean;
  isLoadingEvaluateRule: boolean;

  // Permissions
  hasPreauthorizedPermission: boolean;

  // Custom program state
  customDownPayment: string;
  setCustomDownPayment: (v: string) => void;
  customTerm: string;
  setCustomTerm: (v: string) => void;
  customRecurringAmount: string;
  setCustomRecurringAmount: (v: string) => void;
  showDownPayment: boolean;
  setShowDownPayment: (v: boolean) => void;
  showRecurringAmount: boolean;
  setShowRecurringAmount: (v: boolean) => void;
  showFields: boolean;
  setShowFields: (v: boolean) => void;
  downPaymentError: string;
  recurringAmountError: string;
  isHighRisk: boolean;
  approvalResponse: string;
  setApprovalResponse: (v: string) => void;
  hreApprovalStatus: string;
  setHreApprovalStatus: (v: string) => void;

  // Billing frequency (optimal + custom views)
  billingFrequency: 'monthly' | 'weekly';
  setBillingFrequency: (v: 'monthly' | 'weekly') => void;

  // Custom derived
  customDownPmt: number;
  customTermValue: number;
  customRecurringAmtValue: number;
  calculatedTerm: number;
  /** Calculated term for display/API: when weekly, calculatedTerm (months) × 4 */
  calculatedTermDisplay: number;
  customRecurringAmtFinal: number;

  // Handlers
  createProgramAndNavigate: (
    programData: {
      isPaidInFull: boolean;
      downPmtAmt: number;
      isHighRisk?: boolean;
      terms?: number;
      recurringAmt?: number;
      paymentFreq?: string;
    },
    options?: { navigateTo?: 'pay' | 'custom-results' | 'hre-approval' }
  ) => Promise<void>;
  handleBack: () => void;
  handleOtherPrograms: () => void;
  handleSelectFullPay: () => void;
  handleSelectPreauthorized: (downPaymentAmt?: number, selectedTerm?: number) => void;
  handleSelectProgram: () => void;
  handleSelectOther: () => void;
  handleSelectCustomProgram: () => void;
  handleSelectMinPreapprovedProgram: () => void;
  handleProcessHre: () => void;
  handleDownPaymentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRecurringAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDownPaymentBlur: () => void;
  handleRecurringAmountBlur: () => void;

  // UI
  classes: ReturnType<typeof useStyles>;
  dispatch: AppDispatch;
}

export function usePaymentProgramView(isMobile = false): PaymentProgramViewContext {
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { patientId: urlPatientId, patientEncounterId: urlEncounterId, programType } = useParams<{
    patientId: string;
    patientEncounterId: string;
    programType?: string;
  }>();
  const location = useLocation();
  const isCustomResults = location.pathname.includes('/custom-results');

  const getProgramPath = useCallback(
    (path: string) => {
      if (isMobile) {
        return path.replace('/hub/patient/', '/mobile/patient/');
      }
      return path;
    },
    [isMobile]
  );

  const [fetchedEncounter, setFetchedEncounter] = useState<PatientEncounter | null>(null);
  const [fetchedPatient, setFetchedPatient] = useState<PatientViewModel | null>(null);
  const [fetchedPatientCurrentInsurance, setFetchedPatientCurrentInsurance] = useState<PatientCurrentInsurance | null>(null);
  const [isLoadingEncounter, setIsLoadingEncounter] = useState(false);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isLoadingPatientCurrentInsurance, setIsLoadingPatientCurrentInsurance] = useState(false);
  const [isLoadingRule, setIsLoadingRule] = useState(false);
  const [showDownPayment, setShowDownPayment] = useState<boolean>(false);
  const [showRecurringAmount, setShowRecurringAmount] = useState<boolean>(false);
  const [showFields, setShowFields] = useState<boolean>(false);
  const [approvalResponse, setApprovalResponse] = useState<string>('');
  const [hreApprovalStatus, setHreApprovalStatus] = useState<string>('');
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'weekly'>('monthly');
  const [linkedEstimateAdjustedCharges, setLinkedEstimateAdjustedCharges] = useState<number | null>(null);
  const [linkedEstimateEiv, setLinkedEstimateEiv] = useState<number | null>(null);

  const patient = useSelector((state: RootState) => state?.patientContext?.selectedPatient);
  const patientEncounter = useSelector((state: RootState) => state?.patientContext?.selectedEncounter);
  const selectedPatientCurrentInsurance = useSelector(
    (state: RootState) => state?.patientContext?.selectedPatientCurrentInsurance
  );
  const payersMap = useSelector(
    (state: RootState) => state?.adminContext?.adminConfigurationContext?.payersMap
  );
  const matchingRule = useSelector((state: RootState) => state?.patientContext?.matchingRule);
  const isLoadingEvaluateRule = useSelector((state: RootState) => state?.patientContext?.isLoading?.evaluateRule);
  const authToken = useSelector((state: RootState) => state?.userContext?.userProfile?.tokenState?.jwtIdToken);

  const hasPreauthorizedPermission = useSelector((state: RootState) => {
    const userRolePages = state?.userContext?.userProfile?.userRole?.userRolePages || [];
    return userRolePages.some((page: any) =>
      page?.userRolePageActions?.some(
        (action: any) => action?.sysRolePageAction?.sysRolePageActionId === 99 && action?.isEnabled
      )
    );
  });

  const patientId = urlPatientId ? parseInt(urlPatientId, 10) : 0;
  const encounterId = urlEncounterId ? parseInt(urlEncounterId, 10) : 0;

  // Set channel from URL or default to fms (also handles /mobile routes)
  useHubChannel();

  const programViewKeysRef = useRef<{ patientId?: number; encounterKey?: string }>({});

  const activeEncounter = fetchedEncounter || patientEncounter;
  const activePatient = fetchedPatient || patient;
  const activePatientCurrentInsurance = fetchedPatientCurrentInsurance ?? selectedPatientCurrentInsurance ?? null;
  const pfrAmt = activeEncounter?.pfrAmt || 0;
  const adjustedPfrAmt = activeEncounter?.adjustedPfrAmt ?? pfrAmt;
  const pfrCapturedBalance =
    activeEncounter?.patientPaymentProgram?.[0]?.patientPaymentSchedule?.pfrCapturedBalance ?? adjustedPfrAmt;
  const pfrCollected = adjustedPfrAmt - pfrCapturedBalance;
  const remainingPfr = pfrCapturedBalance;

  const optDownPayment =
    matchingRule?.optimalDownPmtType === '%'
      ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
      : matchingRule?.optimalDownPmt || 0;
  const remainingDownPmt = Math.max(0, optDownPayment - pfrCollected);
  const optimalTermNum = matchingRule?.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
  const optimalTermNumDisplay =
    billingFrequency === 'weekly' ? optimalTermNum * 4 : optimalTermNum;
  const optimalTermDivisor = billingFrequency === 'weekly' ? optimalTermNumDisplay : optimalTermNum;
  const recurringAmt =
    optimalTermDivisor > 0 && pfrAmt > optDownPayment
      ? (pfrAmt - optDownPayment) / optimalTermDivisor
      : 0;

  const currentProgramType = isCustomResults
    ? 'custom'
    : programType === 'hre-approval'
      ? 'custom'
      : programType || 'full';

  // Fetch patient (full details by patientId) — once per patientId per load
  useEffect(() => {
    if (patientId <= 0) {
      setFetchedPatient(null);
      return;
    }
    if (fetchedPatientIds.has(patientId)) return;
    fetchedPatientIds.add(patientId);
    programViewKeysRef.current = { ...programViewKeysRef.current, patientId };
    let cancelled = false;
    (async () => {
      setIsLoadingPatient(true);
      try {
        const res = await patientService.getPatient(patientId);
        if (cancelled) return;
        if (!res.hasErrors && res.entity) {
          setFetchedPatient(res.entity as PatientViewModel);
        } else {
          console.error('Error fetching patient:', res.errorMessage);
        }
      } catch (error) {
        if (!cancelled) console.error('Error fetching patient:', error);
      } finally {
        if (!cancelled) setIsLoadingPatient(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // Fetch patient current insurance (by patientId)
  useEffect(() => {
    (async () => {
      if (patientId > 0) {
        setIsLoadingPatientCurrentInsurance(true);
        try {
          const res = await patientService.getAllPatientCurrentInsurances(patientId);
          if (!res.hasErrors && res.entity) {
            const list = Array.isArray(res.entity) ? res.entity : [];
            setFetchedPatientCurrentInsurance(list.length > 0 ? (list[0] as PatientCurrentInsurance) : null);
          } else {
            setFetchedPatientCurrentInsurance(null);
          }
        } catch (error) {
          console.error('Error fetching patient current insurance:', error);
          setFetchedPatientCurrentInsurance(null);
        } finally {
          setIsLoadingPatientCurrentInsurance(false);
        }
      } else {
        setFetchedPatientCurrentInsurance(null);
      }
    })();
  }, [patientId]);

  // Fetch encounter only for the IOC id in the URL — once per patientId+encounterId per load
  useEffect(() => {
    if (patientId <= 0 || encounterId <= 0) return;
    const encounterKey = `${patientId}-${encounterId}`;
    if (fetchedEncounterKeys.has(encounterKey)) return;
    if (fetchedEncounter?.patientId === patientId && fetchedEncounter?.patientEncounterId === encounterId) return;

    fetchedEncounterKeys.add(encounterKey);
    programViewKeysRef.current = { ...programViewKeysRef.current, encounterKey };
    let cancelled = false;
    (async () => {
      setIsLoadingEncounter(true);
      try {
        const res = await patientService.getPatientInstanceOfCare({
          patientId,
          encounterId,
        });
        if (cancelled) return;
        if (!res.hasErrors && res.entity) {
          setFetchedEncounter(res.entity);
        } else {
          console.error('Error fetching patient encounter:', res.errorMessage);
        }
      } catch (error) {
        if (!cancelled) console.error('Error fetching patient encounter:', error);
      } finally {
        if (!cancelled) setIsLoadingEncounter(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, encounterId, fetchedEncounter?.patientId, fetchedEncounter?.patientEncounterId]);

  // Clear fetch cache when program view unmounts so navigating back refetches
  useEffect(() => {
    return () => {
      const k = programViewKeysRef.current;
      if (k.patientId != null) fetchedPatientIds.delete(k.patientId);
      if (k.encounterKey) fetchedEncounterKeys.delete(k.encounterKey);
    };
  }, []);

  // Evaluate rule
  useEffect(() => {
    (async () => {
      if (patientId > 0 && encounterId > 0 && authToken && fetchedEncounter) {
        setIsLoadingRule(true);
        try {
          const timingRiskId = fetchedEncounter.timingRisk?.timingRiskId;
          const payorRiskId = fetchedEncounter.payorRisk?.payorRiskId;
          const pfrAmt = fetchedEncounter.pfrAmt;
          const optionalParams = [
            typeof timingRiskId === 'number' && `timingRiskId=${timingRiskId}`,
            typeof payorRiskId === 'number' && `payorRiskId=${payorRiskId}`,
            typeof pfrAmt === 'number' && `pfrAmt=${pfrAmt}`,
          ]
            .filter(Boolean)
            .join('&');
          const query = optionalParams
            ? `clientId=${fetchedEncounter.clientId}&facilityId=${fetchedEncounter.facilityId}&${optionalParams}`
            : `clientId=${fetchedEncounter.clientId}&facilityId=${fetchedEncounter.facilityId}`;
          const payload: AxiosReadPayload = {
            dataId: 0,
            url: `payment/v2/client/program/rule?${query}`,
          };
          const matchingRuleRes: AxiosResultStatus = await axiosReadHelper(payload);
          let ruleData: any = null;
          if (!matchingRuleRes.hasErrors && !matchingRuleRes.errorMessage && matchingRuleRes.entity) {
            ruleData = Array.isArray(matchingRuleRes.entity)
              ? matchingRuleRes.entity.length > 0
                ? matchingRuleRes.entity[0]
                : null
              : matchingRuleRes.entity;
          }
          if (ruleData && ruleData.ruleId !== undefined && ruleData.ruleId !== null) {
            dispatch(setMatchingRule(ruleData));
          } else {
            setIsLoadingRule(false);
          }
        } catch (error) {
          console.error('Error calling rule API:', error);
        } finally {
          setIsLoadingRule(false);
        }
      }
    })();
  }, [encounterId, authToken, patientId, fetchedEncounter, dispatch]);

  // Fetch linked estimate (by ioc.pfrEstimateId) for Estimated Adjusted Gross Charges and EIV
  useEffect(() => {
    const pfrEstimateId = fetchedEncounter?.pfrEstimateId;
    if (!pfrEstimateId) {
      setLinkedEstimateAdjustedCharges(null);
      setLinkedEstimateEiv(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await admissionsAdvisorService.callV3EstimateGet(-2, pfrEstimateId);
        if (!cancelled && !res.hasErrors && res.entity) {
          const entity = res.entity as {
            summary?: { totalAdjustedCharges?: number };
            riskAssessment?: { estInsVal?: number };
          };
          const total =
            typeof entity?.summary?.totalAdjustedCharges === 'number'
              ? entity.summary.totalAdjustedCharges
              : null;
          setLinkedEstimateAdjustedCharges(total);
          const eiv =
            typeof entity?.riskAssessment?.estInsVal === 'number' ? entity.riskAssessment.estInsVal : null;
          setLinkedEstimateEiv(eiv);
        } else {
          setLinkedEstimateAdjustedCharges(null);
          setLinkedEstimateEiv(null);
        }
      } catch {
        if (!cancelled) {
          setLinkedEstimateAdjustedCharges(null);
          setLinkedEstimateEiv(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchedEncounter?.pfrEstimateId]);

  // Redirect when no programType
  useEffect(() => {
    if (!programType && !isCustomResults && patientId > 0 && encounterId > 0) {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/full`), { replace: true });
    }
  }, [programType, isCustomResults, patientId, encounterId, navigate, getProgramPath]);

  const [isCreatingProgram, setIsCreatingProgram] = useState(false);

  const addHreOutcomeComment = useCallback(
    async (programDetails?: { downPmtAmt: number; terms: number; recurringAmt: number; billingFreq: 'monthly' | 'weekly' }) => {
      const statusLabel =
        hreApprovalStatus === 'approved'
          ? 'Approved'
          : hreApprovalStatus === 'approved-with-amendment'
            ? 'Approved with Amendment'
            : hreApprovalStatus === 'rejected-by-client'
              ? 'Rejected'
              : hreApprovalStatus === 'rejected-by-guarantor'
                ? 'Rejected by Guarantor'
                : hreApprovalStatus;
      const termDisplay =
        programDetails
          ? programDetails.billingFreq === 'weekly'
            ? `${programDetails.terms * 4} weeks`
            : `${programDetails.terms} months`
          : '';
      const programSection =
        programDetails
          ? [
              `Down Payment: ${formatNumberToUSD(programDetails.downPmtAmt)}`,
              `Term: ${termDisplay}`,
              `Recurring Amount: ${formatNumberToUSD(programDetails.recurringAmt)}`,
            ].join(', ')
          : '';
      const hreNoteText = [
        `HRE outcome: ${statusLabel}.`,
        programSection ? `Program: ${programSection}.` : '',
        approvalResponse?.trim() ? `Response: ${approvalResponse.trim()}` : '',
      ]
        .filter(Boolean)
        .join(' ');
      const hreComment: PatientNotes = {
      patientId: patientId || 0,
      patientEncounterId: encounterId || 0,
      noteText: hreNoteText,
      noteDt: new Date().toISOString(),
      completionDueDt: '',
      patientNotesId: 0,
    };
    await dispatch(
      createPatientNote({
        message: 'HRE outcome recorded',
        comment: hreComment,
        showNotify: false,
      })
    );
    },
    [dispatch, patientId, encounterId, hreApprovalStatus, approvalResponse]
  );

  const createProgramAndNavigate = useCallback(
    async (
      programData: {
        isPaidInFull: boolean;
        downPmtAmt: number;
        isHighRisk?: boolean;
        terms?: number;
        recurringAmt?: number;
        paymentFreq?: string;
      },
      options?: { navigateTo?: 'pay' | 'custom-results' | 'hre-approval' }
    ) => {
      const navigateTo = options?.navigateTo ?? 'pay';
      const isFullPay = programData.isPaidInFull;
      const paymentFreq =
        programData.paymentFreq ||
        (isFullPay ? 'F' : billingFrequency === 'weekly' ? 'W' : 'M');

      const rawTerms = programData.terms ?? 0;
      const termsForApi = billingFrequency === 'weekly' ? rawTerms * 4 : rawTerms;

      const schedulePayload = {
        paymentFreq,
        pfrAmt: pfrAmt || 0,
        pfrBalance: pfrAmt || 0,
        terms: termsForApi,
        remainingTerms: termsForApi,
        paymentDueAmt: programData.recurringAmt || 0,
      };

      const existingProgram = activeEncounter?.patientPaymentProgram?.[0];

      if (existingProgram) {
        setIsCreatingProgram(true);
        try {
          const result = await updateProgram({
            programId: existingProgram.patientPaymentProgramId,
            downPmtAmt: programData.downPmtAmt,
            patientPaymentSchedule: schedulePayload,
            authToken: authToken ?? undefined,
          });
          if (result.success) {
            if (programType === 'hre-approval') {
              await addHreOutcomeComment({
                downPmtAmt: programData.downPmtAmt,
                terms: rawTerms,
                recurringAmt: programData.recurringAmt ?? 0,
                billingFreq: billingFrequency,
              });
            }
            if (navigateTo === 'custom-results') {
              navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom-results`));
            } else if (navigateTo === 'hre-approval') {
              navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/hre-approval`), {
                state: {
                  hreProgramValues: {
                    downPmtAmt: programData.downPmtAmt,
                    terms: rawTerms,
                    recurringAmt: programData.recurringAmt ?? 0,
                    billingFrequency,
                  },
                },
              });
            } else {
              const channelCode = isMobile ? 'mobile' : 'fms';
              const basePath = isMobile
                ? `/mobile/patient/${patientId}/ioc/${encounterId}/pay?ch=${channelCode}`
                : `/hub/patient/${patientId}/ioc/${encounterId}/pay?ch=${channelCode}`;
              navigate(basePath, {
                state: {
                  isProgramSetup: true,
                  programId: existingProgram.patientPaymentProgramId,
                  isPaidInFull: programData.isPaidInFull,
                },
              });
            }
          } else {
            dispatch(showErrorStatus(result.message || 'Failed to update program'));
          }
        } catch (error: any) {
          console.error('Error updating program:', error);
          dispatch(
            showErrorStatus(error?.message || 'Failed to update program')
          );
        } finally {
          setIsCreatingProgram(false);
        }
        return;
      }

      const clientId = activeEncounter?.clientId;
      const facilityId = activeEncounter?.facilityId;
      if (!clientId || !facilityId) {
        dispatch(showErrorStatus('Client ID and Facility ID are required to create a program'));
        return;
      }
      setIsCreatingProgram(true);
      try {
        const result = await createProgram({
          clientId,
          facilityId,
          patientId: patientId || 0,
          patientEncounterId: encounterId || 0,
          isHighRisk: programData.isHighRisk ?? false,
          isPaidInFull: false,
          programPfrAmt: pfrAmt || 0,
          downPmtAmt: programData.downPmtAmt,
          patientPaymentSchedule: schedulePayload,
          authToken: authToken ?? undefined,
        });
        if (result.success && result.programId) {
          if (programType === 'hre-approval') {
            await addHreOutcomeComment({
              downPmtAmt: programData.downPmtAmt,
              terms: rawTerms,
              recurringAmt: programData.recurringAmt ?? 0,
              billingFreq: billingFrequency,
            });
          }
          if (navigateTo === 'custom-results') {
            navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom-results`));
          } else if (navigateTo === 'hre-approval') {
            navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/hre-approval`), {
              state: {
                hreProgramValues: {
                  downPmtAmt: programData.downPmtAmt,
                  terms: rawTerms,
                  recurringAmt: programData.recurringAmt ?? 0,
                  billingFrequency,
                },
              },
            });
          } else {
            const channelCode = isMobile ? 'mobile' : 'fms';
            const basePath = isMobile
              ? `/mobile/patient/${patientId}/ioc/${encounterId}/pay?ch=${channelCode}`
              : `/hub/patient/${patientId}/ioc/${encounterId}/pay?ch=${channelCode}`;
            navigate(basePath, {
              state: { isProgramSetup: true, programId: result.programId, isPaidInFull: programData.isPaidInFull },
            });
          }
        } else {
          dispatch(showErrorStatus(result.message || 'Failed to create program'));
        }
      } catch (error: any) {
        console.error('Error creating program:', error);
        dispatch(
          showErrorStatus(error?.message || 'Failed to create program')
        );
      } finally {
        setIsCreatingProgram(false);
      }
    },
    [
      activeEncounter,
      patientId,
      encounterId,
      isMobile,
      navigate,
      getProgramPath,
      dispatch,
      pfrAmt,
      authToken,
      billingFrequency,
      programType,
      hreApprovalStatus,
      addHreOutcomeComment,
    ]
  );

  const handleSelectFullPay = useCallback(() => {
    createProgramAndNavigate({
      isPaidInFull: true,
      downPmtAmt: pfrAmt,
      isHighRisk: false,
      terms: 0,
      recurringAmt: 0,
      paymentFreq: 'F',
    });
  }, [createProgramAndNavigate, pfrAmt]);

  const handleBack = useCallback(() => {
    if (isCustomResults) {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom`));
      return;
    }
    const currentType = programType || 'full';
    if (currentType === 'custom') {
      navigate(
        getProgramPath(
          `/hub/patient/${patientId}/ioc/${encounterId}/program/${hasPreauthorizedPermission ? 'preauthorized' : 'optimal'}`
        )
      );
    } else if (currentType === 'optimal') {
      navigate(
        getProgramPath(
          `/hub/patient/${patientId}/ioc/${encounterId}/program/${hasPreauthorizedPermission ? 'preauthorized' : 'full'}`
        )
      );
    } else if (currentType === 'preauthorized') {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/full`));
    } else {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/full`));
    }
  }, [isCustomResults, programType, patientId, encounterId, navigate, getProgramPath, hasPreauthorizedPermission]);

  const handleOtherPrograms = useCallback(() => {
    navigate(
      getProgramPath(
        `/hub/patient/${patientId}/ioc/${encounterId}/program/${hasPreauthorizedPermission ? 'preauthorized' : 'optimal'}`
      )
    );
  }, [patientId, encounterId, navigate, getProgramPath, hasPreauthorizedPermission]);

  const handleSelectPreauthorized = useCallback(
    (downPaymentAmt?: number, selectedTerm?: number) => {
      const downPmt = downPaymentAmt ?? optDownPayment ?? 0;
      const termsToUse = selectedTerm ?? optimalTermNum;
      const effectiveTerms = billingFrequency === 'weekly' ? termsToUse * 4 : termsToUse;
      createProgramAndNavigate({
        isPaidInFull: false,
        downPmtAmt: downPmt,
        isHighRisk: false,
        terms: termsToUse,
        recurringAmt:
          effectiveTerms > 0 && pfrAmt > downPmt ? (pfrAmt - downPmt) / effectiveTerms : 0,
      });
    },
    [createProgramAndNavigate, optDownPayment, optimalTermNum, pfrAmt, billingFrequency]
  );

  const handleSelectProgram = useCallback(() => {
    createProgramAndNavigate({
      isPaidInFull: false,
      downPmtAmt: optDownPayment ?? 0,
      isHighRisk: false,
      terms: optimalTermNum,
      recurringAmt: recurringAmt,
    });
  }, [createProgramAndNavigate, optDownPayment, optimalTermNum, recurringAmt]);

  const handleSelectOther = useCallback(() => {
    const currentType = isCustomResults ? 'custom' : programType || 'full';
    if (isMobile && currentType === 'optimal') {
      navigate(`/mobile/patient/${patientId}/ioc/${encounterId}/no-payment-programs`);
    } else {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom`));
    }
  }, [isMobile, isCustomResults, programType, patientId, encounterId, navigate, getProgramPath]);

  const baseContext: Omit<PaymentProgramViewContext, keyof CustomProgramContextAddition> = {
    isMobile,
    programType,
    isCustomResults,
    currentProgramType,
    getProgramPath,
    patientId,
    encounterId,
    navigate,
    activeEncounter,
    activePatient,
    activePatientCurrentInsurance,
    selectedPatientCurrentInsurance,
    payersMap,
    linkedEstimateAdjustedCharges,
    linkedEstimateEiv,
    pfrAmt,
    pfrCollected,
    remainingPfr,
    adjustedPfrAmt,
    matchingRule,
    optDownPayment,
    optimalTermNum,
    optimalTermNumDisplay,
    recurringAmt,
    remainingDownPmt,
    isLoadingEncounter,
    isLoadingPatient,
    isLoadingPatientCurrentInsurance,
    isLoadingRule,
    isLoadingEvaluateRule: isLoadingEvaluateRule ?? false,
    hasPreauthorizedPermission,
    showDownPayment,
    setShowDownPayment,
    showRecurringAmount,
    setShowRecurringAmount,
    showFields,
    setShowFields,
    approvalResponse,
    setApprovalResponse,
    hreApprovalStatus,
    setHreApprovalStatus,
    billingFrequency,
    setBillingFrequency,
    createProgramAndNavigate,
    handleBack,
    handleOtherPrograms,
    handleSelectFullPay,
    handleSelectPreauthorized,
    handleSelectProgram,
    handleSelectOther,
    classes,
    dispatch,
  };

  const custom = useCustomProgramView(baseContext);

  return {
    ...baseContext,
    ...custom,
  } as PaymentProgramViewContext;
}
